import { createSlice } from "@reduxjs/toolkit";
import { format } from "date-fns";
import {
  TIME_FORMAT_OPTIONS,
  DATE_FORMAT_OPTIONS,
  CLIENT_DATE_TIME_DEFAULTS,
} from "../../components/AccessibilityDrawer/utils/constants";
import { getClientDomain } from "../../utils/functions/client-code";

type TimeFormatType = keyof typeof TIME_FORMAT_OPTIONS;
type DateFormatType = (typeof DATE_FORMAT_OPTIONS)[number]["value"];

interface DateTimeFormatState {
  timeFormat: TimeFormatType;
  dateFormat: DateFormatType;
}

const DEFAULT_TIME_FORMAT: TimeFormatType = "24hour";
const DEFAULT_DATE_FORMAT: DateFormatType =
  CLIENT_DATE_TIME_DEFAULTS[getClientDomain()]?.dateFormat ?? "dd MMM yyyy";

const getTimeFormat = (): TimeFormatType => {
  const saved = localStorage.getItem("timeFormat");
  if (saved === "24hour" || saved === "12hour") return saved;
  localStorage.setItem("timeFormat", DEFAULT_TIME_FORMAT);
  return DEFAULT_TIME_FORMAT;
};

const getDateFormat = (): DateFormatType => {
  const saved = localStorage.getItem("dateFormat");
  if (saved && DATE_FORMAT_OPTIONS?.find((option) => option?.value === saved)) {
    return saved as DateFormatType;
  }
  localStorage.setItem("dateFormat", DEFAULT_DATE_FORMAT);
  return DEFAULT_DATE_FORMAT;
};

const initialState: DateTimeFormatState = {
  timeFormat: getTimeFormat(),
  dateFormat: getDateFormat(),
};

export const dateTimeFormatSlice = createSlice({
  name: "dateTimeFormat",
  initialState,
  reducers: {
    setTimeFormat: (state, action: { payload: TimeFormatType }) => {
      state.timeFormat = action.payload;
      localStorage.setItem("timeFormat", action.payload);
    },
    setDateFormat: (state, action: { payload: DateFormatType }) => {
      state.dateFormat = action.payload;
      localStorage.setItem("dateFormat", action.payload);
    },
  },
});

export const { setTimeFormat, setDateFormat } = dateTimeFormatSlice.actions;

export const getDateTimeByFormat = (dateTime?: Date, timezone?: string) => {
  if (!dateTime || isNaN(dateTime?.getTime())) {
    return {
      date: "",
      time: "",
    };
  }
  if (timezone) {
    dateTime = new Date(
      dateTime.toLocaleString("en-US", { timeZone: timezone })
    );
  }

  const timeFormat = getTimeFormat();
  const dateFormat = getDateFormat();

  try {
    const formattedDate = format(dateTime, dateFormat);
    const formattedTime = format(
      dateTime,
      timeFormat === "24hour" ? "HH:mm" : "hh:mm a"
    );

    return {
      date: formattedDate,
      time: formattedTime,
    };
  } catch {
    return {
      date: "",
      time: "",
    };
  }
};

/**
 * Formats a bare 24h "HH:mm" (or "H:mm") time string per the user's 12h/24h
 * preference — the working-hours values are plain strings, not Dates, which is
 * the one case `getDateTimeByFormat` can't take directly. Returns "" for empty
 * input and the original string if it can't be parsed.
 */
export const formatTimeString = (time?: string | null): string => {
  if (!time) return "";
  const [hh, mm] = time.split(":");
  const hourNum = Number(hh);
  const minNum = Number(mm ?? "0");
  if (Number.isNaN(hourNum) || Number.isNaN(minNum)) return time;
  const date = new Date();
  date.setHours(hourNum, minNum, 0, 0);
  return getDateTimeByFormat(date).time || time;
};
